// Staff moderation view for testimonials at /testimonials-mod.
// Lists pending submissions; staff can approve (set tier + status=published),
// reject (status=rejected), or delete outright.

import { useState, useEffect, useMemo } from "react";
import {
  collection, doc, deleteDoc, onSnapshot, orderBy, query, serverTimestamp, updateDoc, where,
} from "firebase/firestore";
import { db } from "../firebase";
import { STAFF_THEME, FONT } from "../theme/staff";

const T = STAFF_THEME;

const TIER_OPTIONS = [
  { key: "guest",  label: "🥞 Guest",  color: "var(--jop-highlight-blue)" },
  { key: "booker", label: "📇 Booker", color: "var(--jop-pink)" },
  { key: "press",  label: "📰 Press",  color: "var(--jop-gold)" },
];

const FILTERS = [
  { key: "pending",   label: "Pending" },
  { key: "published", label: "Published" },
  { key: "rejected",  label: "Rejected" },
];

export default function TestimonialsModeration({ navigate }) {
  const [filter, setFilter] = useState("pending");
  const [items, setItems]   = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const q = query(
      collection(db, "testimonials"),
      where("status", "==", filter),
    );
    const unsub = onSnapshot(q, (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      rows.sort((a, b) => (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0));
      setItems(rows);
      setLoaded(true);
    });
    return unsub;
  }, [filter]);

  const counts = useMemo(() => items.length, [items]);

  return (
    <div style={{ background: T.dark, minHeight: "100vh", color: T.text, fontFamily: FONT.body }}>
      <div style={{ padding: "0.75rem 1rem", background: T.panel, borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
        <button onClick={() => navigate("/menu")} style={btnGhost}>← Menu</button>
        <div style={{ color: T.gold, fontWeight: 700, fontSize: "1.05rem", flex: 1 }}>
          💬 Testimonials
        </div>
        <div style={{ display: "flex", gap: "0.35rem" }}>
          {FILTERS.map((f) => (
            <button key={f.key} onClick={() => setFilter(f.key)} style={filterBtn(filter === f.key)}>
              {f.label}{filter === f.key ? ` · ${counts}` : ""}
            </button>
          ))}
        </div>
      </div>

      <div style={{ maxWidth: "780px", margin: "0 auto", padding: "1rem" }}>
        {!loaded && <p style={{ color: T.muted }}>Loading…</p>}
        {loaded && items.length === 0 && (
          <div style={{ padding: "2rem 1rem", color: T.muted, textAlign: "center", fontSize: "0.95rem" }}>
            {filter === "pending"   && "Nothing in the queue. 🧘"}
            {filter === "published" && "No published testimonials yet."}
            {filter === "rejected"  && "Nothing rejected. Tidy."}
          </div>
        )}
        {items.map((t) => <Card key={t.id} t={t} filter={filter} />)}
      </div>
    </div>
  );
}

function Card({ t, filter }) {
  const [busy, setBusy] = useState(false);
  const [tier, setTier] = useState(t.tier || "guest");

  const approve = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await updateDoc(doc(db, "testimonials", t.id), {
        status: "published",
        tier,
        moderatedAt: serverTimestamp(),
      });
    } catch (e) { alert(e.message); }
    setBusy(false);
  };
  const reject = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await updateDoc(doc(db, "testimonials", t.id), {
        status: "rejected",
        moderatedAt: serverTimestamp(),
      });
    } catch (e) { alert(e.message); }
    setBusy(false);
  };
  const hardDelete = async () => {
    if (busy) return;
    if (!window.confirm("Delete this testimonial permanently?")) return;
    setBusy(true);
    try {
      await deleteDoc(doc(db, "testimonials", t.id));
    } catch (e) { alert(e.message); }
    setBusy(false);
  };
  const revertToPending = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await updateDoc(doc(db, "testimonials", t.id), {
        status: "pending",
      });
    } catch (e) { alert(e.message); }
    setBusy(false);
  };

  return (
    <div style={{
      background: T.panel,
      border: `1px solid ${T.border}`,
      borderRadius: "12px",
      padding: "1rem 1.1rem",
      marginBottom: "0.85rem",
    }}>
      <blockquote style={{
        margin: "0 0 0.7rem",
        color: T.text,
        fontSize: "1rem",
        lineHeight: 1.55,
        fontStyle: "italic",
      }}>
        "{t.text}"
      </blockquote>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem 0.75rem", fontSize: "0.78rem", color: T.muted, marginBottom: "0.85rem" }}>
        {t.authorName && <span><strong style={{ color: T.cream }}>{t.authorName}</strong></span>}
        {t.context && <span>· {t.context}</span>}
        {t.orderId && <span style={{ opacity: 0.6 }}>· order {t.orderId.slice(0, 6)}</span>}
        <span style={{ opacity: 0.6 }}>· {fmtDate(t.createdAt)}</span>
      </div>

      {filter === "pending" && (
        <>
          <div style={{ display: "flex", gap: "0.35rem", marginBottom: "0.7rem", flexWrap: "wrap" }}>
            {TIER_OPTIONS.map((opt) => (
              <button key={opt.key} onClick={() => setTier(opt.key)} style={tierBtn(tier === opt.key, opt.color)}>
                {opt.label}
              </button>
            ))}
          </div>
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            <button onClick={approve} disabled={busy} style={primaryBtn}>
              ✓ Publish
            </button>
            <button onClick={reject} disabled={busy} style={secondaryBtn}>
              Reject
            </button>
            <button onClick={hardDelete} disabled={busy} style={dangerBtn}>
              Delete
            </button>
          </div>
        </>
      )}

      {filter === "published" && (
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <span style={{ fontSize: "0.75rem", color: T.muted }}>Tier:</span>
          {TIER_OPTIONS.map((opt) => (
            <button key={opt.key} onClick={async () => {
              if (opt.key === t.tier) return;
              await updateDoc(doc(db, "testimonials", t.id), { tier: opt.key });
            }} style={tierBtn(t.tier === opt.key, opt.color)}>
              {opt.label}
            </button>
          ))}
          <button onClick={revertToPending} disabled={busy} style={secondaryBtn}>Unpublish</button>
          <button onClick={hardDelete} disabled={busy} style={dangerBtn}>Delete</button>
        </div>
      )}

      {filter === "rejected" && (
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <button onClick={revertToPending} disabled={busy} style={secondaryBtn}>Restore to pending</button>
          <button onClick={hardDelete} disabled={busy} style={dangerBtn}>Delete permanently</button>
        </div>
      )}
    </div>
  );
}

function fmtDate(ts) {
  if (!ts?.toDate) return "—";
  return ts.toDate().toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

const btnGhost = {
  background: "transparent",
  color: T.muted,
  border: `1px solid ${T.border}`,
  borderRadius: "8px",
  padding: "0.35rem 0.75rem",
  fontSize: "0.8rem",
  fontWeight: 600,
  cursor: "pointer",
};

const primaryBtn = {
  background: "#70be44",
  color: "#0a0f1c",
  border: "none",
  borderRadius: "8px",
  padding: "0.5rem 0.9rem",
  fontSize: "0.82rem",
  fontWeight: 700,
  cursor: "pointer",
};

const secondaryBtn = {
  background: "transparent",
  color: T.muted,
  border: `1px solid ${T.border}`,
  borderRadius: "8px",
  padding: "0.5rem 0.9rem",
  fontSize: "0.82rem",
  fontWeight: 600,
  cursor: "pointer",
};

const dangerBtn = {
  background: "transparent",
  color: "#c94444",
  border: `1px solid rgba(201,68,68,0.4)`,
  borderRadius: "8px",
  padding: "0.5rem 0.9rem",
  fontSize: "0.82rem",
  fontWeight: 600,
  cursor: "pointer",
};

function filterBtn(active) {
  return {
    background: active ? "rgba(252,179,31,0.14)" : "transparent",
    color: active ? T.gold : T.muted,
    border: `1px solid ${active ? T.gold : T.border}`,
    borderRadius: "999px",
    padding: "0.35rem 0.75rem",
    fontSize: "0.78rem",
    fontWeight: 600,
    cursor: "pointer",
  };
}

function tierBtn(active, color) {
  return {
    background: active ? `${color}22` : "transparent",
    color: active ? color : "rgba(255,218,173,0.55)",
    border: `1px solid ${active ? color : "rgba(255,218,173,0.2)"}`,
    borderRadius: "8px",
    padding: "0.35rem 0.7rem",
    fontSize: "0.78rem",
    fontWeight: 700,
    cursor: "pointer",
  };
}
